"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";

type ToastKind = "success" | "error" | "info";

type ToastItem = {
  id: number;
  kind: ToastKind;
  message: string;
};

type ToastContextValue = {
  notify: (message: string, kind?: ToastKind) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const t = useTranslations("components");
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(t("toastProviderError"));
  }
  return ctx;
}

const ICONS: Record<ToastKind, string> = {
  success: "✓",
  error: "!",
  info: "i",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useTranslations("components");
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, kind: ToastKind = "success") => {
      const id = Date.now() + Math.random();
      setItems((current) => [...current, { id, kind, message }]);
      setTimeout(() => dismiss(id), 3200);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="cgToastStack" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`cgToast cgToast${item.kind.charAt(0).toUpperCase() + item.kind.slice(1)}`}>
            <span className="cgToastIcon" aria-hidden="true">
              {ICONS[item.kind]}
            </span>
            <span className="cgToastText">{item.message}</span>
            <button
              type="button"
              className="cgToastClose"
              aria-label={t("toastClose")}
              onClick={() => dismiss(item.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
